import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./arrivals.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

// Русские названия типов оплаты -> английские для чека (для англ. языка чека; для русского печатаются как есть)
const PAY_LABELS = [["Наличные", "Cash"], ["Перечисление", "Bank transfer"], ["Терминал", "Terminal"], ["Клик", "Click"], ["СУМ", "UZS"], ["EURO", "EUR"]];
const payLabel = (name, lang) => (lang === "en" ? PAY_LABELS.reduce((str, [ru, en]) => str.replace(ru, en), name || "") : (name || ""));

// Статичные подписи чека (не из data.receipt) на двух языках — тот же текст печатает и планшет (utils/printer.js CHROME)
const CHROME = {
    en: {
        brandSub: "Tour Company", receiptTitle: "Payment receipt", includedTitle: "Included in price",
        orderFallback: "Order #", paymentMethod: "Payment method", card: "Card", amountReceived: "Amount received",
        total: "Total", date: "Date", time: "Time", accepted: "Payment accepted",
        thanks: "Thank you for choosing BesQalaTour!",
    },
    ru: {
        brandSub: "Тур-компания", receiptTitle: "Чек об оплате", includedTitle: "Включено в стоимость",
        orderFallback: "№ заказа", paymentMethod: "Способ оплаты", card: "Карта", amountReceived: "Сумма получена",
        total: "Итого", date: "Дата", time: "Время", accepted: "Оплата принята",
        thanks: "Спасибо, что выбрали BesQalaTour!",
    },
};

// 1234.5 -> "1,234.50"; trim=true убирает ".00" (для сумм в местной валюте)
const money = (v, trim = false) => {
    const n = Number(v) || 0;
    const [int, frac] = n.toFixed(2).split(".");
    const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return trim && frac === "00" ? withCommas : `${withCommas}.${frac}`;
};

// ─────────────────────────────────────────────────────────────
// Печатная форма чека — рендерится всегда, видна только через @media print в arrivals.css
// Строки "details" и "included" приходят с сервера (data.receipt) — тот же набор печатает и планшет
// Экспортируется — её же использует страница «Операции» для повторной печати
export const ReceiptPrintout = ({ data }) => {
    if (!data) return null;
    const lang = data.lang === "en" ? "en" : "ru";
    const C = CHROME[lang];
    // При повторной печати (Операции) — дата/время исходной оплаты, иначе текущие
    const now = data.paidAt ? new Date(data.paidAt) : new Date();
    const details = data.receipt?.[lang]?.details || [{ label: C.orderFallback, value: String(data.orderNumber) }];
    const included = data.receipt?.[lang]?.included || [];
    const foreign = data.currencyCode && data.currencyCode !== "USD";
    const total = foreign ? data.usdAmount : data.amount;
    return createPortal(
        <div className="ar-receipt-portal">
            <div className="ar-receipt-print">
                <div className="ar-receipt-brand">BesQala</div>
                <div className="ar-receipt-brand-sub">{C.brandSub}</div>
                <div className="ar-receipt-sub">{C.receiptTitle}</div>
                <div className="ar-receipt-sep" />

                {details.map((d, i) => (
                    <div className="ar-receipt-row" key={i}><span>{d.label}</span><span>{d.value}</span></div>
                ))}

                {included.length > 0 && (
                    <>
                        <div className="ar-receipt-sep" />
                        <div className="ar-receipt-head">{C.includedTitle}</div>
                        {included.map((d, i) => (
                            <div className="ar-receipt-row" key={i}><span>✓ {d.label}</span><span>{d.value}</span></div>
                        ))}
                    </>
                )}

                <div className="ar-receipt-sep" />

                <div className="ar-receipt-row"><span>{C.paymentMethod}</span><span>{payLabel(data.paymentTypeName, lang)}</span></div>
                {data.kassaRecipient ? <div className="ar-receipt-row"><span>{C.card}</span><span>{data.kassaRecipient}</span></div> : null}
                {foreign ? <div className="ar-receipt-row"><span>{C.amountReceived}</span><span>{money(data.amount, true)} {data.currencyCode}</span></div> : null}

                <div className="ar-receipt-total"><span>{C.total}</span><span>${money(total)}</span></div>

                <div className="ar-receipt-row"><span>{C.date}</span><span>{now.toLocaleDateString("ru-RU")}</span></div>
                <div className="ar-receipt-row"><span>{C.time}</span><span>{now.toLocaleTimeString("ru-RU")}</span></div>

                <div className="ar-receipt-status">✓ {C.accepted}</div>
                <div className="ar-receipt-footer">{C.thanks}</div>
            </div>
        </div>,
        document.body
    );
};

// Модалка выбора языка чека — показывается после успешной оплаты, перед печатью
export const ReceiptLangModal = ({ onPick }) => (
    <div className="ar-lang-overlay">
        <div className="ar-lang-modal">
            <div className="ar-lang-title">Язык чека</div>
            <div className="ar-lang-buttons">
                <button className="ar-lang-btn" onClick={() => onPick("ru")}>Русский</button>
                <button className="ar-lang-btn" onClick={() => onPick("en")}>English</button>
            </div>
            <button className="ar-lang-skip" onClick={() => onPick(null)}>Не печатать</button>
        </div>
    </div>
);

const ArrivalRow = ({ o, API_URL, paymentTypes, kassaList, currencies, onKassaAdded, onPaid, onPrintReceipt, overdue, subtitle }) => {
    const [open, setOpen] = useState(false);
    const [paymentTypeId, setPaymentTypeId] = useState(null);
    const [kassaId, setKassaId] = useState(null);
    const [addCardOpen, setAddCardOpen] = useState(false);
    const [newCardName, setNewCardName] = useState("");
    const [newCardCurrencyId, setNewCardCurrencyId] = useState(null);
    const [savingCard, setSavingCard] = useState(false);

    const [convRate, setConvRate] = useState("");
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const settled = o.status === "paid";

    useEffect(() => {
        if (paymentTypes.length && paymentTypeId == null) setPaymentTypeId(paymentTypes[0].id);
    }, [paymentTypes, paymentTypeId]);

    useEffect(() => {
        if (currencies.length && newCardCurrencyId == null) setNewCardCurrencyId(currencies[0].id);
    }, [currencies, newCardCurrencyId]);

    const selectedType = paymentTypes.find((pt) => pt.id === paymentTypeId);
    const cardsForType = selectedType?.isCard ? kassaList.filter((k) => k.paymentTypeId === paymentTypeId) : [];

    const activeCurrencyCode = selectedType?.isCard
        ? (kassaList.find((k) => k.id === kassaId)?.currencyCode ?? null)
        : (selectedType?.currencyCode ?? null);
    const activeCurrencyName = selectedType?.isCard
        ? (kassaList.find((k) => k.id === kassaId)?.currencyName ?? null)
        : (selectedType?.currencyName ?? null);

    useEffect(() => {
        if (!selectedType?.isCard) { setKassaId(null); return; }
        if (!cardsForType.find((k) => k.id === kassaId)) setKassaId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentTypeId, kassaList]);

    const handleTypeChange = (id) => {
        setPaymentTypeId(id);
        setKassaId(null);
        setAddCardOpen(false);
    };

    const submitNewCard = async () => {
        if (!newCardName.trim()) { toast.error("Укажите получателя — чья карта"); return; }
        setSavingCard(true);
        try {
            await axios.post(`${API_URL}/addKassa`, {
                paymentTypeId: selectedType.id,
                currencyId: newCardCurrencyId,
                recipient: newCardName.trim(),
            });
            toast.success("Карта добавлена");
            setNewCardName("");
            setAddCardOpen(false);
            await onKassaAdded();
        } catch (err) {
            console.error("addKassa:", err);
            toast.error("Не удалось добавить карту");
        } finally {
            setSavingCard(false);
        }
    };

    useEffect(() => {
        if (selectedType?.isCard && cardsForType.length && kassaId == null) {
            setKassaId(cardsForType[cardsForType.length - 1].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kassaList]);

    const convUsd = useMemo(() => {
        if (!activeCurrencyCode || activeCurrencyCode === "USD") return Number(amount) || 0;
        const amt = Number(amount);
        const rate = Number(convRate);
        if (!amt || !rate) return 0;
        return amt / rate;
    }, [amount, convRate, activeCurrencyCode]);

    const submit = async () => {
        if (submitting) return; // защита от двойного клика/повторного вызова
        if (!paymentTypeId) { toast.error("Выберите тип оплаты"); return; }
        if (selectedType?.isCard && !kassaId) { toast.error("Выберите карту"); return; }
        if (!amount || Number(amount) <= 0) { toast.error("Укажите полученную сумму"); return; }
        if (activeCurrencyCode !== "USD" && !convRate) { toast.error(`Укажите курс для конвертации ${activeCurrencyCode} → USD`); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/addFinancePayment`, {
                orderNumber: o.orderNumber,
                requestType: o.requestType,
                paymentTypeId,
                kassaId: selectedType?.isCard ? kassaId : null,
                amount,
                exchangeRate: activeCurrencyCode !== "USD" ? convRate : null,
            });
            toast.success("Оплата принята");

            onPrintReceipt({
                printId: `${o.requestType}-${o.orderNumber}-${Date.now()}`,
                orderNumber: o.orderNumber,
                requestType: o.requestType,
                receipt: o.receipt || null,
                paymentTypeName: selectedType?.name,
                kassaRecipient: selectedType?.isCard ? kassaList.find((k) => k.id === kassaId)?.recipient : null,
                amount,
                currencyCode: activeCurrencyCode,
                usdAmount: activeCurrencyCode !== "USD" ? convUsd.toFixed(2) : null,
            });

            onPaid();
        } catch (err) {
            console.error("addFinancePayment:", err);
            toast.error("Не удалось сохранить оплату");
        } finally {
            setSubmitting(false);
        }
    };

    if (settled) {
        return (
            <div className="ar-row settled">
                <div className="ar-top">
                    <div className="ar-info">
                        <div className="ar-title">{o.title} (№{o.orderNumber})</div>
                        <div className="ar-sub">{subtitle}</div>
                    </div>
                    <span className="fn-pill paid">Оплачено</span>
                    <div className="ar-amount">${fmt(o.grandTotal)}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`ar-row${overdue ? " overdue" : ""}`}>
            <div className="ar-top">
                <div className="ar-info">
                    <div className="ar-title">{o.title} (№{o.orderNumber})</div>
                    <div className="ar-sub">{subtitle}</div>
                </div>
                <span className={`fn-pill ${overdue ? "debt" : "cash"}`}>{overdue ? "Не оплачено" : "Ожидается наличными"}</span>
                <div className="ar-amount">${fmt(o.debtAmount)}</div>
            </div>

            <div className="ar-actions">
                {!open ? <button className="btn-pay" onClick={() => setOpen(true)}>Принять оплату</button> : null}
            </div>

            {open && (
                <div className="pay-panel">
                    <div className="fn-hint" style={{ marginTop: 0 }}>Тип оплаты</div>
                    <div className="fn-paytype">
                        {paymentTypes.map((pt) => (
                            <button key={pt.id} className={paymentTypeId === pt.id ? "selected" : ""} onClick={() => handleTypeChange(pt.id)}>
                                {pt.name}
                            </button>
                        ))}
                    </div>

                    {selectedType?.isCard && (
                        <>
                            <div className="fn-hint">Карта</div>
                            {cardsForType.length > 0 && (
                                <div className="fn-paytype">
                                    {cardsForType.map((k) => (
                                        <button key={k.id} className={kassaId === k.id ? "selected" : ""} onClick={() => setKassaId(k.id)}>
                                            {k.recipient}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    <div className="fn-field" style={{ marginTop: 14 }}>
                        <label>Сумма получена {activeCurrencyCode && activeCurrencyCode !== "USD" ? `(в ${activeCurrencyCode})` : ""}</label>
                        <input type="number" placeholder={o.debtAmount} value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>

                    {activeCurrencyCode && activeCurrencyCode !== "USD" && (
                        <div className="fn-convert-panel">
                            <div className="fn-convert-note">
                                Касса ведётся в {activeCurrencyName} — укажите курс, чтобы система засчитала бронь как оплаченную в USD. Баланс кассы пополнится в её родной валюте.
                            </div>
                            <div className="fn-fgrid two">
                                <div className="fn-field"><label>Курс (1 USD = )</label><input type="number" placeholder="12 700" value={convRate} onChange={(e) => setConvRate(e.target.value)} /></div>
                                <div className="fn-field"><label>Итого в USD</label><input value={convUsd ? convUsd.toFixed(2) : ""} readOnly /></div>
                            </div>
                        </div>
                    )}

                    <button className="fn-btn-add-payment" onClick={submit} disabled={submitting} style={{ marginTop: 12 }}>
                        {submitting ? "Сохраняем…" : "Подтвердить оплату"}
                    </button>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
function TourArrivals({ API_URL, paymentTypes, kassaList, currencies, onKassaAdded, onPrintReceipt }) {
    const [today, setToday] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getTourArrivals`);
            if (data.status === "success") { setToday(data.today); setOverdue(data.overdue); }
        } catch (err) {
            console.error("getTourArrivals:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const paidCount = today.filter((o) => o.status === "paid").length;
    const pendingCount = today.filter((o) => o.status !== "paid").length;

    const subtitleFor = (o, overdueFlag) =>
        `Оператор ${o.manager || "—"} · ${o.participantsCount ? `${o.participantsCount} чел · ` : ""}${overdueFlag ? "выезд уже был" : "выезд сегодня"}`;

    return (
        <>
            <div className="ar-summary">
                <div className="fn-stat"><div className="fn-lbl">Туров начинается сегодня</div><div className="fn-val">{today.length}</div></div>
                <div className="fn-stat paid"><div className="fn-lbl">Оплачено</div><div className="fn-val">{paidCount}</div></div>
                <div className="fn-stat debt"><div className="fn-lbl">Ожидает оплаты</div><div className="fn-val">{pendingCount}</div></div>
            </div>


            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : (
                <>
                    <div className="fn-section-label">Начинаются сегодня ({today.length})</div>
                    {today.length === 0 ? (
                        <div className="fn-empty">На сегодня туров с наличной оплатой нет</div>
                    ) : (
                        today.map((o) => (
                            <ArrivalRow
                                key={`${o.requestType}-${o.orderNumber}`}
                                o={{ ...o, title: `${o.tour} — ${o.title}` }}
                                subtitle={subtitleFor(o, false)}
                                API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies}
                                onKassaAdded={onKassaAdded} onPaid={load} onPrintReceipt={onPrintReceipt} overdue={false}
                            />
                        ))
                    )}

                    {overdue.length > 0 && (
                        <>
                            <div className="fn-section-label">Неоплаченные поездки — тур уже начался ({overdue.length})</div>
                            {overdue.map((o) => (
                                <ArrivalRow
                                    key={`${o.requestType}-${o.orderNumber}`}
                                    o={{ ...o, title: `${o.tour} — ${o.title}` }}
                                    subtitle={subtitleFor(o, true)}
                                    API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies}
                                    onKassaAdded={onKassaAdded} onPaid={load} onPrintReceipt={onPrintReceipt} overdue={true}
                                />
                            ))}
                        </>
                    )}
                </>
            )}
        </>
    );
}

// ─────────────────────────────────────────────────────────────
function PropertyArrivals({ API_URL, paymentTypes, kassaList, currencies, onKassaAdded, onPrintReceipt }) {
    const [today, setToday] = useState([]);
    const [overdue, setOverdue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [propertyFilter, setPropertyFilter] = useState("manual_yurt");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getPropertyArrivals`);
            if (data.status === "success") { setToday(data.today); setOverdue(data.overdue); }
        } catch (err) {
            console.error("getPropertyArrivals:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const filteredToday = today.filter((o) => o.requestType === propertyFilter);
    const filteredOverdue = overdue.filter((o) => o.requestType === propertyFilter);

    const receivedToday = filteredToday.filter((o) => o.status === "paid").length;
    const pendingToday = filteredToday.filter((o) => o.status !== "paid").length;

    const subtitleFor = (o) =>
        `${o.requestType === "manual_guesthouse" ? "Гостевой дом" : "Юрт-лагерь"} · ${o.participantsCount ? `${o.participantsCount} чел · ` : ""}${o.date ? new Date(o.date).toLocaleDateString("ru-RU") : ""}`;

    return (
        <>
          

            <div className="fn-toolbar" style={{ marginBottom: 18 }}>
                <span className={`fn-chip${propertyFilter === "manual_yurt" ? " active" : ""}`} onClick={() => setPropertyFilter("manual_yurt")}>Юрт-лагерь</span>
                <span className={`fn-chip${propertyFilter === "manual_guesthouse" ? " active" : ""}`} onClick={() => setPropertyFilter("manual_guesthouse")}>Гостевой дом</span>
            </div>

            <div className="ar-summary">
                <div className="fn-stat"><div className="fn-lbl">Заездов сегодня</div><div className="fn-val">{filteredToday.length}</div></div>
                <div className="fn-stat paid"><div className="fn-lbl">Оплачено</div><div className="fn-val">{receivedToday}</div></div>
                <div className="fn-stat debt"><div className="fn-lbl">Ожидает оплаты</div><div className="fn-val">{pendingToday}</div></div>
            </div>

            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : (
                <>
                    <div className="fn-section-label">Заезжают сегодня ({filteredToday.length})</div>
                    {filteredToday.length === 0 ? (
                        <div className="fn-empty">На сегодня заездов нет</div>
                    ) : (
                        filteredToday.map((o) => (
                            <ArrivalRow
                                key={`${o.requestType}-${o.orderNumber}`}
                                o={o} subtitle={subtitleFor(o)}
                                API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies}
                                onKassaAdded={onKassaAdded} onPaid={load} onPrintReceipt={onPrintReceipt} overdue={false}
                            />
                        ))
                    )}

                    {filteredOverdue.length > 0 && (
                        <>
                            <div className="fn-section-label">Не оплачено — заезд уже был ({filteredOverdue.length})</div>
                            {filteredOverdue.map((o) => (
                                <ArrivalRow
                                    key={`${o.requestType}-${o.orderNumber}`}
                                    o={o} subtitle={subtitleFor(o)}
                                    API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies}
                                    onKassaAdded={onKassaAdded} onPaid={load} onPrintReceipt={onPrintReceipt} overdue={true}
                                />
                            ))}
                        </>
                    )}
                </>
            )}
        </>
    );
}

// ─────────────────────────────────────────────────────────────
const Arrivals = ({ API_URL }) => {
    const [tab, setTab] = useState("tour");
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [kassaList, setKassaList] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [pendingReceipt, setPendingReceipt] = useState(null); // оплата прошла, ждём выбора языка чека
    const [receipt, setReceipt] = useState(null); // язык выбран — этот объект и печатается
    const printedIdRef = React.useRef(null);

    const loadKassaData = useCallback(async () => {
        try {
            const [ptRes, kassaRes, curRes] = await Promise.all([
                axios.post(`${API_URL}/getPaymentTypes`),
                axios.post(`${API_URL}/getKassaList`),
                axios.post(`${API_URL}/getCurrencies`),
            ]);
            if (ptRes.data.status === "success") setPaymentTypes(ptRes.data.paymentTypes);
            if (kassaRes.data.status === "success") setKassaList(kassaRes.data.kassaList);
            if (curRes.data.status === "success") setCurrencies(curRes.data.currencies);
        } catch (err) {
            console.error("loadKassaData:", err);
        }
    }, [API_URL]);

    useEffect(() => { loadKassaData(); }, [loadKassaData]);

    // После установки receipt даём React отрисовать чек в DOM, затем печатаем.
    // printedIdRef защищает от повторной печати одного и того же чека —
    // в dev-режиме React.StrictMode дважды прогоняет этот эффект.
    useEffect(() => {
        if (!receipt) return;
        if (printedIdRef.current === receipt.printId) return;
        printedIdRef.current = receipt.printId;
        const t = setTimeout(() => {
            window.print();
            setReceipt(null);
        }, 400);
        return () => clearTimeout(t);
    }, [receipt]);

    const handleLangPick = (lang) => {
        if (lang) setReceipt({ ...pendingReceipt, lang });
        setPendingReceipt(null);
    };

    return (
        <div className="finance">
            <div className="fn-topbar">
                <div className="fn-pagetitle">Заезды сегодня</div>
            </div>

            <div className="fn-tabs">
                <div className={`fn-tab${tab === "tour" ? " active" : ""}`} onClick={() => setTab("tour")}>Заезды тура</div>
                <div className={`fn-tab${tab === "property" ? " active" : ""}`} onClick={() => setTab("property")}>Заезды в жильё</div>
            </div>

            {tab === "tour"
                ? <TourArrivals API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies} onKassaAdded={loadKassaData} onPrintReceipt={setPendingReceipt} />
                : <PropertyArrivals API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} currencies={currencies} onKassaAdded={loadKassaData} onPrintReceipt={setPendingReceipt} />
            }

            {pendingReceipt ? <ReceiptLangModal onPick={handleLangPick} /> : null}
            <ReceiptPrintout data={receipt} />
        </div>
    );
};

export default Arrivals;